/**
 * RecapSection - Tournament summary + schedule generation for Americano
 */
import React, { useState } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import AmericanoSchedulerV2 from '../../services/scheduler';
import firebaseSyncService from '../../services/firebaseSync';

const RecapSection = ({ onGenerate }) => {
  const { state, setSchedule, exportState } = useTournament();
  const { config, players, scheduleGenerated } = state;
  const { currentUser, isSignedIn } = useAuth();
  const { showSuccess, showError } = useToast();

  const [generating, setGenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [openConfig, setOpenConfig] = useState(false);
  const [openCourts, setOpenCourts] = useState(false);
  const [openPlayers, setOpenPlayers] = useState(false);

  const canGenerate = players.length >= 4 && players.length % 4 === 0;

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const result = await AmericanoSchedulerV2.generateBestOfN(
        players,
        config.courtCount,
        config.roundCount,
        config.pointsPerMatch,
        3
      );
      const { schedule, seed } = result;
      setSchedule(schedule, seed);

      // Save to Firebase
      if (isSignedIn() && currentUser) {
        try {
          await new Promise(resolve => setTimeout(resolve, 150));
          const tournamentState = exportState();
          tournamentState.scheduleGenerated = true;
          await firebaseSyncService.saveTournament(tournamentState, currentUser.email, 'planning-generated');
          console.log('✅ Planning sauvegardé dans Firebase');
        } catch (err) {
          console.warn('Could not save to Firebase:', err);
        }
      }

      showSuccess('Planning généré !');
      if (onGenerate) setTimeout(onGenerate, 300);
    } catch (err) {
      console.error('Error generating schedule:', err);
      showError('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (scheduleGenerated) {
      setShowConfirm(true);
    } else {
      doGenerate();
    }
  };

  const totalTime =
    (config.warmupDuration || 0) +
    (config.roundCount || 0) * (config.matchDuration || 0) +
    ((config.roundCount || 0) > 1 ? ((config.roundCount || 0) - 1) * (config.pauseDuration || 0) : 0);

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1><i className="bi bi-file-earmark-text me-3"></i>Récapitulatif</h1>
        <p className="lead">Vérifiez avant de générer le planning</p>
      </div>

      <div className="section-body">

        {/* Generate Button */}
        <div className="generate-section text-center mb-4">
          {scheduleGenerated ? (
            <button
              type="button"
              className="btn btn-lg btn-warning btn-generate"
              onClick={handleGenerateClick}
              disabled={generating || !canGenerate}
            >
              {generating ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Re-génération...</>
              ) : (
                <><i className="bi bi-arrow-repeat me-2"></i>Re-générer le planning</>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-lg btn-success btn-generate"
              onClick={handleGenerateClick}
              disabled={generating || !canGenerate}
            >
              {generating ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Génération en cours...</>
              ) : (
                <><i className="bi bi-play-fill me-2"></i>Générer le planning</>
              )}
            </button>
          )}
        </div>

        {/* Config recap */}
        <div className="recap-card mb-4">
          <h3 className="recap-card-header" onClick={() => setOpenConfig(o => !o)}>
            <span><i className="bi bi-gear me-2"></i>Configuration</span>
            <i className={`bi bi-chevron-down recap-chevron ${openConfig ? 'open' : ''}`}></i>
          </h3>
          {openConfig && (
            <ul className="recap-list">
              <li>
                <span className="label">Joueurs</span>
                <span className="value">{config.playerCount}</span>
              </li>
              <li>
                <span className="label">Terrains</span>
                <span className="value">{config.courtCount}</span>
              </li>
              <li>
                <span className="label">Tours</span>
                <span className="value">{config.roundCount}</span>
              </li>
              <li>
                <span className="label">Points / match</span>
                <span className="value">{config.pointsPerMatch}</span>
              </li>
              <li>
                <span className="label">Durée estimée</span>
                <span className="value">{totalTime} min</span>
              </li>
              <li>
                <span className="label">Heure de début</span>
                <span className="value">{config.startTimeStr}</span>
              </li>
            </ul>
          )}
        </div>

        {/* Terrains */}
        <div className="recap-card mb-4">
          <h3 className="recap-card-header" onClick={() => setOpenCourts(o => !o)}>
            <span><i className="bi bi-grid-3x3-gap-fill me-2"></i>Terrains</span>
            <i className={`bi bi-chevron-down recap-chevron ${openCourts ? 'open' : ''}`}></i>
          </h3>
          {openCourts && (
            <ul className="recap-list">
              {(config.courtNames || []).map((name, i) => (
                <li key={i}>
                  <span className="label">Terrain {i + 1}</span>
                  <span className="value">{name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Players */}
        <div className="recap-card mb-4">
          <h3 className="recap-card-header" onClick={() => setOpenPlayers(o => !o)}>
            <span><i className="bi bi-people-fill me-2"></i>Joueurs ({players.length})</span>
            <i className={`bi bi-chevron-down recap-chevron ${openPlayers ? 'open' : ''}`}></i>
          </h3>
          {openPlayers && (
            players.length === 0 ? (
              <p className="text-muted small">Aucun joueur enregistré</p>
            ) : (
              <div className="players-recap">
                <div className="d-flex flex-wrap gap-2">
                  {players.map((p, i) => (
                    <span key={i} className="badge badge-player">{p.name}</span>
                  ))}
                </div>
              </div>
            )
          )}
        </div>

        {/* Warnings */}
        {!canGenerate && players.length > 0 && (
          <div className="alert alert-warning mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Le nombre de joueurs doit être un multiple de 4 pour l'Americano 2v2.
            Actuellement : <strong>{players.length}</strong> joueurs.
          </div>
        )}
        {players.length === 0 && (
          <div className="alert alert-warning mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Ajoutez des joueurs avant de générer le planning.
          </div>
        )}

      </div>

      {/* Confirm re-generate modal */}
      {showConfirm && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                    Re-générer ?
                  </h5>
                  <button className="btn-close" onClick={() => setShowConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">Le planning actuel et tous les résultats saisis seront effacés.</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Annuler</button>
                  <button
                    className="btn btn-warning"
                    onClick={() => { setShowConfirm(false); doGenerate(); }}
                  >
                    Re-générer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecapSection;
